import { type RegisteredComponent, isEditing } from "@builder.io/sdk-react";

import { useLayoutData } from "~/routes/layout";

interface LmnSuppressedContentProps {
  children?: React.ReactNode;
  attributes?: Record<string, unknown>;
}

/**
 * LmnSuppressedContent — renders children only when the session is NOT
 * LMN-allowed (i.e. a TPA user whose AFID is NOT on the lmnListAllowsTPA
 * allow list). This is the inverse of LmnAllowedContent.
 *
 * Use this block to wrap content that should be hidden FROM LMN sessions —
 * e.g. standard pricing, promotions, or messaging that doesn't apply to
 * Letter of Medical Necessity users.
 *
 * Ported from hec-composable:
 *   hec/builder/blocks/content-by-access/lmnSuppressedContent.tsx
 * Workers data source: layout loader → useLayoutData().lmnAllowed
 */
const LmnSuppressedContent = ({
  children,
  attributes,
}: LmnSuppressedContentProps) => {
  const { lmnAllowed } = useLayoutData();
  // In Builder visual editor, always render children so authors can reach and
  // edit suppressed content regardless of the current session's LMN status.
  if (!isEditing() && lmnAllowed) return null;
  return <div {...attributes}>{children}</div>;
};

export const LmnSuppressedContentDefinition = {
  component: LmnSuppressedContent,
  name: "LmnSuppressedContent",
  noWrap: true,
  canHaveChildren: true,
  image: "https://unpkg.com/lucide-static@0.537.0/icons/calendar-off.svg",
  inputs: [],
} satisfies RegisteredComponent;

export default LmnSuppressedContent;
